/* eslint-disable react/prop-types */
import React, {
  useCallback,
  CSSProperties,
  PropsWithChildren,
  useRef
} from "react";
import * as styles from "./StickyMenu.module.scss";
import {
  BackgroundColor,
  Button,
  classForBackground
} from "@silversea-ssc/ui-components-react";
import clsx from "clsx";
import { dequal } from "dequal/lite";
import { useSticky } from "./useSticky";
import { useScrollSpy } from "./useScrollSpy";
import { scrollToSectionId } from "../../utils/inPageNavigation";
import { useHorizontalScroll } from "src/utils/useHorizontalScroll";
import { useIsTabletOrDesktop } from "src/utils/useIsTabletOrDesktop";

export type StickyMenuSectionRef = {
  id: string;
  title: string;
  trackingName?: string;
};

export interface StickyMenuProps {
  sectionRefs: StickyMenuSectionRef[];
  translateDesktopTop?: number;
  spacerTabletOrDesktop?: boolean;
  backgroundColor: BackgroundColor;
  variant?: "floating" | "fixed";
}

const StickyMenu: React.FC<
  React.PropsWithChildren<PropsWithChildren<StickyMenuProps>>
> = ({
  sectionRefs,
  translateDesktopTop = 0,
  spacerTabletOrDesktop = false,
  children,
  backgroundColor,
  variant = "floating"
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const isTabletOrDesktop = useIsTabletOrDesktop();

  const onSectionVisibileChanged = useCallback(
    (sectionId) => {
      const container = navRef.current;
      if (!container) return;

      const activeLink = document.getElementById(`nav-${sectionId}`);
      const scrollPosition = computeScrollPosition(activeLink, container);

      container.scrollTo({
        left: scrollPosition,
        behavior: "smooth"
      });
    },
    [navRef]
  );

  const spyElements = sectionRefs.map((r) => ({ id: r.id }));
  const isSticky = useSticky<HTMLElement>(menuRef, 0);
  const visibleSection = useScrollSpy(spyElements, onSectionVisibileChanged);
  const bgClass = classForBackground(backgroundColor);

  const { isAtEnd, isAtStart } = useHorizontalScroll(navContainerRef, isSticky);

  const cssVariables = {
    "--translate-desktop-top": `-${translateDesktopTop}px`
  } as CSSProperties;

  return (
    <React.Fragment>
      <div
        ref={menuRef}
        className={clsx(styles.menu, {
          [styles.menuFixed]: variant == "fixed",
          [styles.menuFloating]: variant == "floating",
          [styles.menuSticky]: isSticky || visibleSection
        })}
        style={cssVariables}
        data-content-name="sticky_menu"
      >
        {children && (
          <div
            className={styles.summary}
            data-secondary-sticky-menu-children="true"
          >
            {children}
          </div>
        )}
        <div className={styles.menuContainer} data-secondary-sticky-menu="true">
          <div className={styles.navWrapper} ref={navContainerRef}>
            {!isAtStart && (
              <div
                className={clsx(styles.leftFade, { [styles.sticky]: isSticky })}
              ></div>
            )}
            {!isAtEnd && (
              <div
                className={clsx(styles.rightFade, {
                  [styles.sticky]: isSticky
                })}
              ></div>
            )}
            <nav
              ref={navRef}
              className={styles.nav}
              data-content-name="sticky_menu"
            >
              <React.Fragment>
                {sectionRefs.map((section, index) => (
                  <NavItem
                    id={`nav-${section.id}`}
                    key={index}
                    section={section}
                    visibleSection={visibleSection}
                  />
                ))}
              </React.Fragment>
            </nav>
          </div>
        </div>
      </div>
      {spacerTabletOrDesktop && isTabletOrDesktop && (
        <div className={clsx(styles.spacer, bgClass)}></div>
      )}
    </React.Fragment>
  );
};

type NavItemProps = {
  id: string;
  section: StickyMenuSectionRef;
  visibleSection: string | undefined;
};

const NavItem: React.FC<React.PropsWithChildren<NavItemProps>> = ({
  section,
  visibleSection,
  id
}) => {
  const onClick = useCallback(() => {
    scrollToSectionId(section.id);
  }, [section.id]);

  return (
    <div
      id={id}
      className={clsx(styles.navItem, {
        [styles.navItemSelected]: section.id == visibleSection
      })}
    >
      <Button
        full
        variant="text"
        renderAs="button"
        onClick={onClick}
        dataContentPiece={section.trackingName}
      >
        {section.title}
      </Button>
    </div>
  );
};

export default React.memo(StickyMenu, dequal);

const computeScrollPosition = (
  el: HTMLElement | null,
  container: HTMLElement
): number =>
  el ? el.offsetLeft + el.offsetWidth / 2 - container.offsetWidth / 2 : 0;
